// src/components/DigitalCard/DigitalCardSettings.js
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import './DigitalCardSettings.module.css';

/**
 * DigitalCardSettings component
 * Allows users to manage their digital card preferences with weighted signals
 */
const DigitalCardSettings = () => {
  // Access user context for data and update functions
  const { 
    user, 
    updateUserPreferences, 
    updateSignalWeight, 
    addInfluence, 
    removeInfluence, 
    updateScoreThreshold 
  } = useContext(UserContext);
  
  const [signalCategories, setSignalCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('preferences');
  
  // Local state for form inputs
  const [preferences, setPreferences] = useState({
    preferredTags: [],
    avoidedTags: [],
    influences: [],
    materialBias: [],
    colorPalette: [],
    styleBias: [],
    scoreThreshold: 0.6,
    signalWeights: {}
  });
  
  // Status message for saving
  const [saveStatus, setSaveStatus] = useState('');
  
  // Load signal categories from JSON file
  useEffect(() => {
    const loadSignals = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Load signal categories from JSON file
        const response = await fetch('/signals.json');
        if (!response.ok) {
          throw new Error(`Failed to load signals: ${response.statusText}`);
        }
        
        const data = await response.json();
        setSignalCategories(data.categories || []);
        
        // Initialize preferences from user data
        if (user && user.curationSignature) {
          setPreferences({
            preferredTags: user.curationSignature.preferredTags || [],
            avoidedTags: user.curationSignature.avoidedTags || [],
            influences: user.curationSignature.influences || [],
            materialBias: user.curationSignature.materialBias || [],
            colorPalette: user.curationSignature.colorPalette || [],
            styleBias: user.curationSignature.styleBias || [],
            scoreThreshold: user.curationSignature.scoreThreshold || 0.6,
            signalWeights: user.curationSignature.signalWeights || {}
          });
        }
      } catch (err) { // Changed variable name from 'error' to 'err' to avoid shadowing
        console.error('Error loading signals:', err);
        setError(`Failed to load signal categories: ${err.message}`);
        
        // Use mock data if loading fails
        const mockCategories = [
          {
            id: "styles",
            name: "Design Styles",
            signals: ["Minimalist", "Brutalist", "Mid-century Modern", "Industrial", "Scandinavian", "Japanese", "Art Deco", "Modern", "Traditional", "Contemporary"]
          },
          {
            id: "materials",
            name: "Materials",
            signals: ["Concrete", "Wood", "Glass", "Steel", "Marble", "Brass", "Leather", "Ceramic", "Stone", "Plaster"]
          },
          {
            id: "colors",
            name: "Color Palettes",
            signals: ["Monochrome", "Neutral", "Earth Tones", "Pastels", "Bold Colors", "Black & White", "Warm", "Cool", "Muted", "Vibrant"]
          },
          {
            id: "spaces",
            name: "Spaces",
            signals: ["Residential", "Commercial", "Hospitality", "Outdoor", "Urban", "Rural", "Studio", "Gallery", "Workspace", "Kitchen"]
          }
        ];
        
        setSignalCategories(mockCategories);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSignals();
  }, [user]);
  
  // Handle signal weight adjustment
  const handleSignalWeightChange = (signal, value) => {
    // Convert value to integer
    const weightValue = parseInt(value, 10);
    
    // Update local state
    setPreferences(prev => ({
      ...prev,
      signalWeights: {
        ...prev.signalWeights,
        [signal]: weightValue
      }
    }));
    
    // Update user context if function available
    if (updateSignalWeight) {
      updateSignalWeight(signal, weightValue);
    }
  };
  
  // Get current weight for a signal (-5 to +5)
  const getSignalWeight = (signal) => {
    // Check local state first
    if (preferences.signalWeights && preferences.signalWeights[signal] !== undefined) {
      return preferences.signalWeights[signal];
    }
    
    // Check user context
    if (user?.curationSignature?.signalWeights && 
        user.curationSignature.signalWeights[signal] !== undefined) {
      return user.curationSignature.signalWeights[signal];
    }
    
    // Fallback: Check traditional preferences
    if (preferences.avoidedTags && preferences.avoidedTags.includes(signal)) {
      return -3; // Default negative preference
    }
    
    if (preferences.preferredTags && preferences.preferredTags.includes(signal)) return 3;
    if (preferences.styleBias && preferences.styleBias.includes(signal)) return 3;
    if (preferences.materialBias && preferences.materialBias.includes(signal)) return 3;
    if (preferences.colorPalette && preferences.colorPalette.includes(signal)) return 3;
    if (preferences.influences && preferences.influences.includes(signal)) return 5;
    
    // Default: neutral
    return 0;
  };
  
  // Handle threshold slider change
  const handleThresholdChange = (event) => {
    const value = parseFloat(event.target.value);
    
    // Update local state
    setPreferences(prev => ({
      ...prev,
      scoreThreshold: value
    }));
    
    // Update user context if function available
    if (updateScoreThreshold) {
      updateScoreThreshold(value);
    }
  };
  
  // Add a custom influence
  const [newInfluenceInput, setNewInfluenceInput] = useState('');
  
  const handleAddInfluence = () => {
    if (!newInfluenceInput.trim()) return;
    
    const influenceName = newInfluenceInput.trim();
    
    // Update local state
    setPreferences(prev => ({
      ...prev,
      influences: [...prev.influences, influenceName],
      signalWeights: { ...prev.signalWeights, [influenceName]: 5 }
    }));
    
    // Update user context if function available
    if (addInfluence) {
      addInfluence(influenceName);
    }
    
    // Clear input
    setNewInfluenceInput('');
  };
  
  // Remove an influence
  const handleRemoveInfluence = (influence) => {
    // Update local state
    setPreferences(prev => {
      const updatedInfluences = prev.influences.filter(item => item !== influence);
      const { [influence]: removed, ...updatedWeights } = prev.signalWeights;
      
      return {
        ...prev,
        influences: updatedInfluences,
        signalWeights: updatedWeights
      };
    });
    
    // Update user context if function available
    if (removeInfluence) {
      removeInfluence(influence);
    }
  };
  
  // Add a custom tag
  const [newTag, setNewTag] = useState('');
  const [tagType, setTagType] = useState('preferred');
  
  const handleAddCustomTag = () => {
    if (!newTag.trim()) return;
    
    const tagName = newTag.trim();
    const weightValue = tagType === 'preferred' ? 3 : -3;
    
    // Update local state
    setPreferences(prev => {
      const updatedPrefs = { ...prev };
      
      if (tagType === 'preferred') {
        updatedPrefs.preferredTags = [...prev.preferredTags, tagName];
        // Remove from avoided if present
        updatedPrefs.avoidedTags = prev.avoidedTags.filter(tag => tag !== tagName);
      } else {
        updatedPrefs.avoidedTags = [...prev.avoidedTags, tagName];
        // Remove from preferred if present
        updatedPrefs.preferredTags = prev.preferredTags.filter(tag => tag !== tagName);
      }
      
      // Update signal weights
      updatedPrefs.signalWeights = {
        ...prev.signalWeights,
        [tagName]: weightValue
      };
      
      return updatedPrefs;
    });
    
    // Update user context if function available
    if (updateSignalWeight) {
      updateSignalWeight(tagName, weightValue);
    }
    
    // Clear input
    setNewTag('');
  };
  
  // Save all preferences
  const handleSavePreferences = () => {
    try {
      // Update user preferences if function available
      if (updateUserPreferences) {
        updateUserPreferences(preferences);
        setSaveStatus('Preferences saved successfully!');
      } else {
        setSaveStatus('Preferences saved successfully (demo mode)');
      }
      
      // Clear status after delay
      setTimeout(() => {
        setSaveStatus('');
      }, 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      setSaveStatus(`Error: ${error.message}`);
      
      setTimeout(() => {
        setSaveStatus('');
      }, 3000);
    }
  };
  
  // Render a signal weight slider
  const renderWeightSlider = (signal, category) => {
    const weight = getSignalWeight(signal);
    
    return (
      <div className="signal-item" key={signal}>
        <span className="signal-name">{signal}</span>
        
        <div className="signal-slider-container">
          <input 
            type="range"
            min="-5"
            max="5"
            step="1"
            value={weight}
            onChange={(e) => handleSignalWeightChange(signal, e.target.value)}
            className={`signal-slider weight-${weight > 0 ? 'positive' : weight < 0 ? 'negative' : 'neutral'}`}
          />
          
          <span className="weight-value">
            {weight > 0 ? `+${weight}` : weight}
          </span>
        </div>
        
        <div className="signal-actions">
          <button 
            className={`action-button negative ${weight < 0 ? 'active' : ''}`}
            onClick={() => handleSignalWeightChange(signal, weight < 0 ? '0' : '-3')}
            title={weight < 0 ? 'Reset to neutral' : 'Set to dislike'}
          >
            –
          </button>
          
          <button 
            className={`action-button neutral ${weight === 0 ? 'active' : ''}`}
            onClick={() => handleSignalWeightChange(signal, '0')}
            title="Set to neutral"
          >
            ○
          </button>
          
          <button 
            className={`action-button positive ${weight > 0 ? 'active' : ''}`}
            onClick={() => handleSignalWeightChange(signal, weight > 0 ? '0' : '3')}
            title={weight > 0 ? 'Reset to neutral' : 'Set to like'}
          >
            +
          </button>
        </div>
      </div>
    );
  };
  
  if (isLoading) {
    return <div className="loading">Loading your Digital Card preferences...</div>;
  }
  
  return (
    <div className="digital-card-settings">
      <div className="settings-header">
        <h2>Digital Card Preferences</h2>
        <p className="settings-description">
          Your Digital Card acts as your aesthetic profile, helping to filter content based on your tastes.
          Adjust the sliders to set your preferences from -5 (strongly dislike) to +5 (strongly like).
        </p>
      </div>
      
      <div className="settings-tabs">
        <button 
          className={`tab-button ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          Signal Weights
        </button>
        <button 
          className={`tab-button ${activeTab === 'influences' ? 'active' : ''}`}
          onClick={() => setActiveTab('influences')}
        >
          Influences
        </button>
        <button 
          className={`tab-button ${activeTab === 'custom' ? 'active' : ''}`}
          onClick={() => setActiveTab('custom')}
        >
          Custom Tags
        </button>
        <button 
          className={`tab-button ${activeTab === 'threshold' ? 'active' : ''}`}
          onClick={() => setActiveTab('threshold')}
        >
          Filter Threshold
        </button>
      </div>
      
      <div className="settings-content">
        {/* Signal Weights Tab */}
        {activeTab === 'preferences' && (
          <div className="preferences-tab">
            <p className="tab-description">
              Adjust the sliders to set your preferences. Positive values increase the likelihood of 
              seeing related content, while negative values reduce it.
            </p>
            
            {signalCategories.map((category) => (
              <div key={category.id} className="category-section">
                <h3 className="category-title">{category.name}</h3>
                
                <div className="signals-grid">
                  {category.signals.map((signal) => renderWeightSlider(signal, category.name))}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Influences Tab */}
        {activeTab === 'influences' && (
          <div className="influences-tab">
            <p className="tab-description">
              Add designers, architects, artists, or other influences that inspire your aesthetic.
              Influences have a stronger weight in your Digital Card.
            </p>
            
            <div className="add-influence-form">
              <input 
                type="text"
                placeholder="Enter an influence (e.g., 'Luis Barragán')"
                value={newInfluenceInput}
                onChange={(e) => setNewInfluenceInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddInfluence()}
              />
              <button 
                onClick={handleAddInfluence}
                disabled={!newInfluenceInput.trim()}
              >
                Add Influence
              </button>
            </div>
            
            <div className="influences-list">
              {preferences.influences && preferences.influences.length > 0 ? (
                <div className="influence-tags">
                  {preferences.influences.map((influence) => (
                    <div key={influence} className="influence-tag">
                      <span className="influence-name">{influence}</span>
                      <span className="influence-weight">+5</span>
                      <button 
                        className="remove-button"
                        onClick={() => handleRemoveInfluence(influence)}
                        aria-label={`Remove ${influence}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-message">
                  No influences added yet. Add some to enhance your Digital Card.
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Custom Tags Tab */}
        {activeTab === 'custom' && (
          <div className="custom-tags-tab">
            <p className="tab-description">
              Add your own custom tags to further refine your Digital Card preferences.
            </p>
            
            <div className="add-tag-form">
              <input 
                type="text"
                placeholder="Enter a custom tag (e.g., 'Japanese Gardens')"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCustomTag()}
              />
              
              <div className="tag-type-toggle">
                <label>
                  <input 
                    type="radio"
                    name="tagType"
                    value="preferred"
                    checked={tagType === 'preferred'}
                    onChange={() => setTagType('preferred')}
                  />
                  <span className="toggle-label preferred">Like (+3)</span>
                </label>
                
                <label>
                  <input 
                    type="radio"
                    name="tagType"
                    value="avoided"
                    checked={tagType === 'avoided'}
                    onChange={() => setTagType('avoided')}
                  />
                  <span className="toggle-label avoided">Dislike (-3)</span>
                </label>
              </div>
              
              <button 
                onClick={handleAddCustomTag}
                disabled={!newTag.trim()}
              >
                Add Tag
              </button>
            </div>
            
            <div className="tags-container">
              <div className="tag-section">
                <h3>Liked Tags</h3>
                <div className="tags-list">
                  {preferences.preferredTags && preferences.preferredTags.length > 0 ? (
                    <div className="tag-items">
                      {preferences.preferredTags.map((tag) => {
                        const weight = preferences.signalWeights[tag] || 3;
                        return (
                          <div key={tag} className="tag-item preferred">
                            <span className="tag-name">{tag}</span>
                            <span className="tag-weight">+{weight}</span>
                            <button 
                              className="remove-button"
                              onClick={() => handleSignalWeightChange(tag, '0')}
                              aria-label={`Remove ${tag}`}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="empty-message">
                      No liked tags added yet.
                    </p>
                  )}
                </div>
              </div>
              
              <div className="tag-section">
                <h3>Disliked Tags</h3>
                <div className="tags-list">
                  {preferences.avoidedTags && preferences.avoidedTags.length > 0 ? (
                    <div className="tag-items">
                      {preferences.avoidedTags.map((tag) => {
                        const weight = preferences.signalWeights[tag] || -3;
                        return (
                          <div key={tag} className="tag-item avoided">
                            <span className="tag-name">{tag}</span>
                            <span className="tag-weight">{weight}</span>
                            <button 
                              className="remove-button"
                              onClick={() => handleSignalWeightChange(tag, '0')}
                              aria-label={`Remove ${tag}`}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="empty-message">
                      No disliked tags added yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Threshold Tab */}
        {activeTab === 'threshold' && (
          <div className="threshold-tab">
            <p className="tab-description">
              Set the filter threshold for your Digital Card. This determines how closely content must match
              your preferences to appear in your curated feed.
            </p>
            
            <div className="threshold-control">
              <div className="threshold-header">
                <span>Filter Threshold: {preferences.scoreThreshold.toFixed(2)}</span>
              </div>
              
              <div className="threshold-slider-container">
                <span className="threshold-label">More Content</span>
                <input 
                  type="range"
                  min="0.1"
                  max="0.9"
                  step="0.05"
                  value={preferences.scoreThreshold}
                  onChange={handleThresholdChange}
                  className="threshold-slider"
                />
                <span className="threshold-label">More Focused</span>
              </div>
              
              <div className="threshold-description">
                <p>
                  <strong>Lower values (0.1 - 0.3):</strong> Show more varied content with some relation to your preferences.
                </p>
                <p>
                  <strong>Medium values (0.4 - 0.6):</strong> Balanced approach with good preference matching.
                </p>
                <p>
                  <strong>Higher values (0.7 - 0.9):</strong> Strictly filtered content that closely matches your preferences.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Digital Card Preview */}
      <div className="digital-card-preview">
        <h3>Your Digital Card</h3>
        
        <div className="card-preview-content">
          <div className="card-header">
            <h4>{user?.displayName || 'Your Name'}</h4>
            <p className="card-bio">{user?.bio || 'Design enthusiast'}</p>
          </div>
          
          <div className="card-preferences">
            {Object.entries(preferences.signalWeights || {})
              .filter(([_, weight]) => parseInt(weight) > 0)
              .sort((a, b) => parseInt(b[1]) - parseInt(a[1]))
              .slice(0, 5)
              .map(([signal, weight]) => (
                <span key={signal} className="card-tag positive">
                  {signal} +{weight}
                </span>
              ))}
              
            {Object.entries(preferences.signalWeights || {})
              .filter(([_, weight]) => parseInt(weight) < 0)
              .sort((a, b) => parseInt(a[1]) - parseInt(b[1]))
              .slice(0, 3)
              .map(([signal, weight]) => (
                <span key={signal} className="card-tag negative">
                  {signal} {weight}
                </span>
              ))}
              
            {Object.keys(preferences.signalWeights || {}).length === 0 && (
              <p className="no-preferences">No preferences set yet</p>
            )}
          </div>
        </div>
      </div>
      
      <div className="settings-actions">
        <button 
          className="save-button"
          onClick={handleSavePreferences}
        >
          Save Preferences
        </button>
        
        {saveStatus && (
          <div className={`save-status ${saveStatus.includes('Error') ? 'error' : 'success'}`}>
            {saveStatus}
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalCardSettings;